import {
	ApartmentOutlined,
	CheckCircleFilled,
	CrownOutlined,
	EditOutlined,
	FilterOutlined,
	InfoCircleOutlined,
	LeftOutlined,
	RightOutlined,
	SearchOutlined,
	UserOutlined,
	WarningOutlined,
} from '@ant-design/icons'
import {
	Avatar,
	Badge,
	Button,
	Card,
	Checkbox,
	Drawer,
	Empty,
	Input,
	List,
	Select,
	Space,
	Spin,
	Tabs,
	Tag,
	Tooltip,
	Typography,
	message,
} from 'antd'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { staffService } from '../../common/service/staff.service'
import { userService } from '../../common/service/user.service'
import { useTheme, useThemeColors } from '../../common/ThemeContext'

interface StaffOrgChartProps {
	onClose?: () => void
	viewMode?: 'summary' | 'detail'
	hideInternalHeader?: boolean
}

interface OrgStaff {
	id: string
	nameKh: string
	nameEn: string
	phoneNumber: string
	ranking?: string
	photoUrl?: string
	departmentId?: string
	role?: { name: string }
	firstLineManager?: { id: string; name: string; photoUrl?: string }
	secondLineManager?: { id: string; name: string; photoUrl?: string }
	thirdLineManager?: { id: string; name: string; photoUrl?: string }
}

type ManagerRef = { id: string; name: string; photoUrl?: string }

interface OrgNode {
	staff: OrgStaff
	children: OrgNode[]
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CONNECTOR_HEIGHT = 32

// ─── Components ──────────────────────────────────────────────────────────────

const OrgLegend: React.FC<{ theme: any; primary: string }> = ({ theme, primary }) => (
	<div
		style={{
			display: 'flex',
			gap: 16,
			padding: '8px 16px',
			background: theme.bg,
			borderRadius: 12,
			border: `1px solid ${theme.border}`,
			marginBottom: 16,
			flexWrap: 'wrap',
			alignItems: 'center',
		}}
	>
		<Typography.Text strong style={{ fontSize: 12, marginRight: 4, color: theme.textSecondary }}>
			RANKING:
		</Typography.Text>
		<Space size={12}>
			<Badge color='#9333ea' text={<span style={{ fontSize: 11, color: theme.textSecondary }}>L3 Manager</span>} />
			<Badge color='#ef4444' text={<span style={{ fontSize: 11, color: theme.textSecondary }}>L2 Manager</span>} />
			<Badge color='#f59e0b' text={<span style={{ fontSize: 11, color: theme.textSecondary }}>L1 Manager</span>} />
			<Badge color={primary} text={<span style={{ fontSize: 11, color: theme.textSecondary }}>Executor</span>} />
		</Space>
		<div style={{ height: 16, width: 1, background: theme.border, margin: '0 8px' }} />
		<Space size={12}>
			<span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
				<CheckCircleFilled style={{ color: '#22c55e', fontSize: 12 }} />
				<Typography.Text style={{ fontSize: 11, color: theme.textSecondary }}>Full Chain Verified</Typography.Text>
			</span>
		</Space>
	</div>
)

const ViewModeSwitcher: React.FC<{
	value: 'summary' | 'detail'
	onChange: (v: 'summary' | 'detail') => void
	theme: any
	primary: string
}> = ({ value, onChange, theme, primary }) => {
	const modes = [
		{ key: 'detail', label: 'Detail' },
		{ key: 'summary', label: 'Summary' },
	]

	return (
		<div
			style={{
				display: 'flex',
				background: theme.subBg,
				padding: 3,
				borderRadius: 10,
				position: 'relative',
				border: `1px solid ${theme.border}`,
				width: 160,
				height: 32,
				cursor: 'pointer',
				userSelect: 'none',
			}}
		>
			<motion.div
				layoutId='mode-pill'
				transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
				style={{
					position: 'absolute',
					top: 2,
					bottom: 2,
					left: value === 'detail' ? 2 : '50%',
					width: 'calc(50% - 2px)',
					background: theme.bg,
					borderRadius: 8,
					boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
					zIndex: 1,
				}}
			/>
			{modes.map((m) => (
				<div
					key={m.key}
					onClick={() => onChange(m.key as any)}
					style={{
						flex: 1,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						zIndex: 2,
						fontSize: 12,
						fontWeight: value === m.key ? 700 : 500,
						color: value === m.key ? primary : theme.textSecondary,
						transition: 'color 0.3s ease',
					}}
				>
					{m.label}
				</div>
			))}
		</div>
	)
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const rankOrder = (r?: string) => {
	if (r === 'manager_level_3') return 0
	if (r === 'manager_level_2') return 1
	if (r === 'manager_level_1') return 2
	if (r === 'executor') return 3
	return 4
}

const toRankingLabel = (value?: string) => {
	if (value === 'manager_level_3') return 'Manager L3'
	if (value === 'manager_level_2') return 'Manager L2'
	if (value === 'manager_level_1') return 'Manager L1'
	if (value === 'executor') return 'Executor'
	return value || ''
}

const toRankingColor = (value?: string, brandPrimary?: string) => {
	if (value === 'manager_level_3') return '#9333ea'
	if (value === 'manager_level_2') return '#ef4444'
	if (value === 'manager_level_1') return '#f59e0b'
	if (value === 'executor') return brandPrimary || '#254a89'
	return 'default'
}

/** Executor with 1st, 2nd, and 3rd line managers all set (org chart card badge). */
const isExecutorWithFullManagerChain = (staff: OrgStaff) =>
	staff.ranking === 'executor' &&
	!!staff.firstLineManager?.id &&
	!!staff.secondLineManager?.id &&
	!!staff.thirdLineManager?.id

/** Higher = more senior. L3 (top) … Executor (bottom). Used to validate manager chain order. */
const RANKING_SENIORITY: Record<string, number> = {
	manager_level_3: 4,
	manager_level_2: 3,
	manager_level_1: 2,
	executor: 1,
}

const seniorityScore = (ranking: string | undefined): number => {
	if (!ranking) return 1
	return RANKING_SENIORITY[ranking] ?? 1
}

const toDisplayName = (staff?: Pick<OrgStaff, 'nameEn' | 'nameKh' | 'id'> | null) => {
	if (!staff) return ''
	return staff.nameEn || staff.nameKh || staff.id
}

type RankingTier = 'executor' | 'manager_level_1' | 'manager_level_2' | 'manager_level_3'

const RANKING_TIER_STRENGTH: Record<RankingTier, number> = {
	executor: 0,
	manager_level_1: 1,
	manager_level_2: 2,
	manager_level_3: 3,
}

const maxRankingTier = (current: string | undefined, candidate: RankingTier): RankingTier => {
	const cur = (current as RankingTier) || 'executor'
	const a = RANKING_TIER_STRENGTH[cur] ?? 0
	const b = RANKING_TIER_STRENGTH[candidate]
	return b > a ? candidate : cur
}

/** Walk FLM → their FLM → their FLM to fill 2nd / 3rd line (cycle-safe). */
const resolveSecondThirdFromFirstLine = (
	selfId: string,
	first: ManagerRef | null,
	byId: Map<string, OrgStaff>,
): { second: ManagerRef | null; third: ManagerRef | null } => {
	if (!first?.id) return { second: null, third: null }
	const visited = new Set<string>([selfId.trim(), first.id.trim()].filter(Boolean))

	const flmStaff = byId.get(first.id)
	const secondId = (flmStaff?.firstLineManager?.id || '').trim()
	if (!secondId || visited.has(secondId)) return { second: null, third: null }
	visited.add(secondId)
	const secondStaff = byId.get(secondId)
	const second: ManagerRef = {
		id: secondId,
		name: toDisplayName(secondStaff) || flmStaff?.firstLineManager?.name || secondId,
		photoUrl: secondStaff?.photoUrl || flmStaff?.firstLineManager?.photoUrl,
	}

	const thirdId = (secondStaff?.firstLineManager?.id || '').trim()
	if (!thirdId || visited.has(thirdId)) return { second, third: null }
	visited.add(thirdId)
	const thirdStaff = byId.get(thirdId)
	const third: ManagerRef = {
		id: thirdId,
		name: toDisplayName(thirdStaff) || secondStaff?.firstLineManager?.name || thirdId,
		photoUrl: thirdStaff?.photoUrl || secondStaff?.firstLineManager?.photoUrl,
	}
	return { second, third }
}

type StaffDetailPayload = {
	firstLineManager?: { id?: string; name?: string; photoUrl?: string }
	nameInEnglish?: string
	firstName?: string
	lastName?: string
	photoUrl?: string
	ranking?: string
}

const RANKING_SELECT_OPTIONS: { value: RankingTier; label: string }[] = [
	{ value: 'executor', label: 'Executor' },
	{ value: 'manager_level_1', label: 'Manager L1' },
	{ value: 'manager_level_2', label: 'Manager L2' },
	{ value: 'manager_level_3', label: 'Manager L3' },
]

const isRankingTier = (v: string | undefined): v is RankingTier =>
	v === 'executor' ||
	v === 'manager_level_1' ||
	v === 'manager_level_2' ||
	v === 'manager_level_3'

/** Subject staff ranking when saving with auto-ranking: L1 manager ⇒ executor; else L1 if they have direct reports. */
const computeSubjectRanking = (
	staffId: string,
	firstLineMgrId: string | undefined,
	firstLineMgrRanking: string | undefined,
	staffList: OrgStaff[],
): RankingTier => {
	const fid = (firstLineMgrId || '').trim()
	if (fid && firstLineMgrRanking === 'manager_level_1') {
		return 'executor'
	}
	const hasDirectReports = staffList.some(
		(s) => s.id !== staffId && s.firstLineManager?.id === staffId,
	)
	return hasDirectReports ? 'manager_level_1' : 'executor'
}

/** Same chain as sync resolver, but loads `firstLineManager` via staff detail when missing from `byId` (list payloads are often incomplete). */
const resolveSecondThirdFromFirstLineAsync = async (
	selfId: string,
	first: ManagerRef | null,
	byId: Map<string, OrgStaff>,
	fetchDetail: (id: string) => Promise<StaffDetailPayload | null>,
): Promise<{ second: ManagerRef | null; third: ManagerRef | null }> => {
	if (!first?.id) return { second: null, third: null }

	const sync = resolveSecondThirdFromFirstLine(selfId, first, byId)
	if (sync.third?.id) return sync

	const visited = new Set<string>([selfId.trim(), first.id.trim()].filter(Boolean))
	const flmIdOf = (staffId: string) => (byId.get(staffId)?.firstLineManager?.id || '').trim()

	const buildRef = async (mgrId: string, embedded?: { name?: string; photoUrl?: string }) => {
		const row = byId.get(mgrId)
		if (row) {
			return {
				id: mgrId,
				name: toDisplayName(row) || embedded?.name || mgrId,
				photoUrl: row.photoUrl || embedded?.photoUrl,
			} as ManagerRef
		}
		const d = await fetchDetail(mgrId)
		const name =
			toDisplayName({
				nameEn: d?.nameInEnglish || '',
				nameKh: `${d?.lastName || ''} ${d?.firstName || ''}`.trim(),
				id: mgrId,
			}) ||
			embedded?.name ||
			mgrId
		return { id: mgrId, name, photoUrl: d?.photoUrl || embedded?.photoUrl } as ManagerRef
	}

	const flmStaff = byId.get(first.id)

	// Second already resolved from list; third missing — usually 2nd line's FLM not on list rows.
	if (sync.second?.id && !sync.third?.id) {
		const secondId = sync.second.id
		if (visited.has(secondId)) return { second: sync.second, third: null }
		visited.add(secondId)
		let thirdId = flmIdOf(secondId)
		let secondDetail: StaffDetailPayload | null = null
		if (!thirdId) {
			secondDetail = await fetchDetail(secondId)
			thirdId = (secondDetail?.firstLineManager?.id || '').trim()
		}
		if (!thirdId || visited.has(thirdId)) return { second: sync.second, third: null }
		visited.add(thirdId)
		const secondRow = byId.get(secondId)
		const embedded =
			(secondRow?.firstLineManager?.id === thirdId ? secondRow.firstLineManager : undefined) ||
			(secondDetail?.firstLineManager?.id === thirdId ? secondDetail.firstLineManager : undefined)
		const third = await buildRef(thirdId, embedded)
		return { second: sync.second, third }
	}

	let secondId = flmIdOf(first.id)
	if (!secondId) {
		const d = await fetchDetail(first.id)
		secondId = (d?.firstLineManager?.id || '').trim()
	}
	if (!secondId || visited.has(secondId)) return { second: null, third: null }
	visited.add(secondId)
	const second = await buildRef(secondId, flmStaff?.firstLineManager)

	let thirdId = flmIdOf(secondId)
	if (!thirdId) {
		const d2 = await fetchDetail(secondId)
		thirdId = (d2?.firstLineManager?.id || '').trim()
	}
	if (!thirdId || visited.has(thirdId)) return { second, third: null }
	visited.add(thirdId)
	const secondRow = byId.get(secondId)
	const third = await buildRef(thirdId, secondRow?.firstLineManager)
	return { second, third }
}

// ─── Build Tree ───────────────────────────────────────────────────────────────

function buildTree(staff: OrgStaff[]): OrgNode[] {
	const byManager = new Map<string, OrgStaff[]>()
	const rootStaff: OrgStaff[] = []

	staff.forEach((s) => {
		if (!s.firstLineManager?.id) {
			rootStaff.push(s)
		} else {
			const mgr = s.firstLineManager.id
			if (!byManager.has(mgr)) byManager.set(mgr, [])
			byManager.get(mgr)!.push(s)
		}
	})

	const buildNode = (s: OrgStaff): OrgNode => ({
		staff: s,
		children: (byManager.get(s.id) || [])
			.sort((a, b) => rankOrder(a.ranking) - rankOrder(b.ranking))
			.map(buildNode),
	})

	return rootStaff.sort((a, b) => rankOrder(a.ranking) - rankOrder(b.ranking)).map(buildNode)
}

// ─── Org Tree Node ────────────────────────────────────────────────────────────

interface OrgTreeNodeProps {
	node: OrgNode
	onEdit: (s: OrgStaff) => void
	theme: any
	viewMode: 'summary' | 'detail'
	isFirst?: boolean
	isLast?: boolean
	isRoot?: boolean
}

const OrgTreeNode: React.FC<OrgTreeNodeProps> = ({
	node,
	onEdit,
	theme,
	viewMode,
	isFirst = true,
	isLast = true,
	isRoot = false,
}) => {
	const { primary } = useThemeColors()
	const hasChildren = node.children.length > 0
	const isSummary = viewMode === 'summary'
	const childSlotPadding = isSummary ? '0 8px' : '0 16px'
	const verticalConnectorHeight = isSummary ? 20 : CONNECTOR_HEIGHT

	const rankingColor = toRankingColor(node.staff.ranking, primary)

	return (
		<div
			style={{
				display: 'inline-flex',
				flexDirection: 'column',
				alignItems: 'center',
				padding: childSlotPadding,
				position: 'relative',
			}}
		>
			{/* Horizontal connector segment at the top of this child slot */}
			{!isRoot && (
				<div
					style={{
						position: 'absolute',
						top: 0,
						left: isFirst ? '50%' : 0,
						right: isLast ? '50%' : 0,
						height: 2,
						background: theme.line,
					}}
				/>
			)}

			{/* Vertical drop from horizontal connector (or from parent card if root) */}
			{!isRoot && (
				<div
					style={{
						width: 2,
						height: verticalConnectorHeight,
						background: theme.line,
						flexShrink: 0,
					}}
				/>
			)}

			{/* Staff Card / Summary Node */}
			<motion.div
				layout
				initial={{ opacity: 0, scale: 0.9 }}
				animate={{ opacity: 1, scale: 1 }}
				whileHover={{ y: -4, boxShadow: theme.cardHoverShadow }}
				transition={{ duration: 0.3, ease: 'easeOut' }}
				style={{ zIndex: 2 }}
			>
				{isSummary ? (
					<Tooltip
						title={
							<div style={{ padding: '4px' }}>
								<div style={{ fontWeight: 'bold' }}>{node.staff.nameEn || node.staff.nameKh}</div>
								<div style={{ fontSize: '11px', opacity: 0.8 }}>ID: {node.staff.id}</div>
								<div style={{ fontSize: '11px', opacity: 0.8 }}>{toRankingLabel(node.staff.ranking)}</div>
								{node.staff.role?.name && <div style={{ fontSize: '11px', opacity: 0.8 }}>{node.staff.role.name}</div>}
							</div>
						}
					>
						<div
							style={{
								position: 'relative',
								width: 110,
								padding: '12px 8px',
								textAlign: 'center',
								flexShrink: 0,
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
								backgroundColor: theme.bg,
								borderRadius: 14,
								border: `1px solid ${theme.border}`,
								borderTop: `3px solid ${rankingColor}`,
								boxShadow: theme.cardShadow,
								cursor: 'pointer',
								transition: 'border-color 0.2s',
							}}
							onClick={() => onEdit(node.staff)}
							onMouseEnter={(e) => (e.currentTarget.style.borderColor = rankingColor)}
							onMouseLeave={(e) => (e.currentTarget.style.borderColor = theme.border)}
						>
							{isExecutorWithFullManagerChain(node.staff) && (
								<CheckCircleFilled
									style={{
										position: 'absolute',
										top: 6,
										right: 6,
										color: '#22c55e',
										fontSize: 11,
										zIndex: 1,
									}}
								/>
							)}
							<Avatar
								src={node.staff.photoUrl}
								icon={<UserOutlined />}
								size={36}
								style={{
									marginBottom: 8,
									border: `1px solid ${theme.line}`,
									boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
								}}
							/>
							<Typography.Text
								strong
								ellipsis
								style={{
									display: 'block',
									maxWidth: '100%',
									fontSize: 11,
									lineHeight: '1.2',
									color: theme.text,
									marginBottom: 2,
								}}
							>
								{node.staff.nameEn || node.staff.nameKh || node.staff.id}
							</Typography.Text>
							<Typography.Text
								type='secondary'
								style={{
									fontSize: 9,
									display: 'block',
								}}
							>
								ID: {node.staff.id}
							</Typography.Text>
						</div>
					</Tooltip>
				) : (
					<Card
						size='small'
						bordered={false}
						style={{
							position: 'relative',
							width: 220,
							minHeight: 280,
							textAlign: 'center',
							borderRadius: 20,
							backgroundColor: theme.bg,
							flexShrink: 0,
							boxShadow: theme.cardShadow,
							border: `1px solid ${theme.border}`,
							display: 'flex',
							flexDirection: 'column',
							overflow: 'hidden',
						}}
						bodyStyle={{
							padding: '0',
							display: 'flex',
							flexDirection: 'column',
							flex: 1,
						}}
					>
						{/* Header with Background */}
						<div
							style={{
								height: 50,
								width: '100%',
								background: `linear-gradient(180deg, ${rankingColor}22 0%, ${theme.bg} 100%)`,
								borderBottom: `1px solid ${theme.line}`,
								position: 'relative',
							}}
						>
							<div
								style={{
									position: 'absolute',
									top: 0,
									left: 0,
									right: 0,
									height: 4,
									background: rankingColor,
								}}
							/>
						</div>

						<div style={{ padding: '0 16px 16px', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
							{isExecutorWithFullManagerChain(node.staff) && (
								<Tooltip title='Full Manager Chain (L1, L2, L3) assigned'>
									<CheckCircleFilled
										style={{
											position: 'absolute',
											top: 12,
											right: 12,
											color: '#22c55e',
											fontSize: 18,
											zIndex: 1,
											filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
										}}
									/>
								</Tooltip>
							)}

							<Avatar
								src={node.staff.photoUrl}
								icon={<UserOutlined />}
								size={72}
								style={{
									marginTop: -36,
									marginBottom: 12,
									border: `4px solid ${theme.bg}`,
									boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
									backgroundColor: theme.subBg,
								}}
							/>

							<div style={{ textAlign: 'center', marginBottom: 12, width: '100%' }}>
								<Typography.Text
									strong
									style={{
										display: 'block',
										fontSize: 16,
										lineHeight: '1.2',
										color: theme.text,
									}}
								>
									{node.staff.nameEn || node.staff.nameKh || node.staff.id}
								</Typography.Text>
								{node.staff.nameKh && node.staff.nameEn && (
									<Typography.Text
										style={{
											display: 'block',
											fontSize: 12,
											color: theme.textSecondary,
											marginTop: 2,
										}}
									>
										{node.staff.nameKh}
									</Typography.Text>
								)}
								<Typography.Text
									style={{
										display: 'block',
										fontSize: 11,
										color: theme.textTertiary,
										marginTop: 4,
										letterSpacing: '0.5px',
										fontFamily: 'monospace',
									}}
								>
									ID: {node.staff.id}
								</Typography.Text>
							</div>

							<div
								style={{
									marginBottom: 16,
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									gap: 4,
									width: '100%',
								}}
							>
								{node.staff.ranking && (
									<Tag
										color={toRankingColor(node.staff.ranking, primary)}
										style={{ fontSize: 10, borderRadius: 12, margin: 0, border: 'none', padding: '1px 12px', fontWeight: 700, textTransform: 'uppercase' }}
									>
										{toRankingLabel(node.staff.ranking)}
									</Tag>
								)}
								{node.staff.role?.name && (
									<Typography.Text
										ellipsis
										style={{ fontSize: 12, color: theme.textSecondary, fontWeight: 500, marginTop: 2 }}
									>
										{node.staff.role.name}
									</Typography.Text>
								)}
							</div>

						</div>

						<div style={{ borderTop: `1px solid ${theme.border}`, backgroundColor: `${primary}08` }}>
							<Button
								type='text'
								size='middle'
								icon={<EditOutlined />}
								style={{
									width: '100%',
									height: 40,
									fontSize: 13,
									color: primary,
									fontWeight: 600,
									borderRadius: 0,
								}}
								onClick={() => onEdit(node.staff)}
							>
								Manage Line
							</Button>
						</div>
					</Card>
				)}
			</motion.div>

			{/* Children */}
			{hasChildren && (
				<>
					{/* Vertical line from card bottom down to children's horizontal connector */}
					<div
						style={{
							width: 2,
							height: verticalConnectorHeight,
							background: theme.line,
							flexShrink: 0,
						}}
					/>
					{/* Children row */}
					<div
						style={{
							display: 'inline-flex',
							flexDirection: 'row',
							alignItems: 'flex-start',
						}}
					>
						{node.children.map((child, idx) => (
							<OrgTreeNode
								key={child.staff.id}
								node={child}
								onEdit={onEdit}
								theme={theme}
								viewMode={viewMode}
								isFirst={idx === 0}
								isLast={idx === node.children.length - 1}
							/>
						))}
					</div>
				</>
			)}
		</div>
	)
}

// ─── Lookup Results List ─────────────────────────────────────────────────────

interface LookupListProps {
	results: { id: string; name: string; nameKh?: string; photoUrl?: string }[]
	onSelect: (r: { id: string; name: string; photoUrl?: string }) => void
	mutedColor?: string
}

const LookupList: React.FC<LookupListProps> = ({ results, onSelect, mutedColor = '#888' }) => (
	<List
		size='small'
		bordered
		style={{ marginTop: 4, maxHeight: 200, overflow: 'auto' }}
		dataSource={results}
		renderItem={(r) => (
			<List.Item
				style={{ cursor: 'pointer' }}
				onClick={() => onSelect({ id: r.id, name: r.name, photoUrl: r.photoUrl })}
			>
				<Space>
					<Avatar src={r.photoUrl} icon={<UserOutlined />} size='small' />
					<span>
						<strong>{r.id}</strong> — {r.name}
						{r.nameKh && (
							<span style={{ color: mutedColor, marginLeft: 6, fontSize: 12 }}>
								{r.nameKh}
							</span>
						)}
					</span>
				</Space>
			</List.Item>
		)}
	/>
)

// ─── Manager Assign Card ─────────────────────────────────────────────────────

interface ManagerCardProps {
	label: string
	current: ManagerRef | null
	lookupActive: boolean
	lookupSearch: string
	lookupResults: { id: string; name: string; nameKh?: string; photoUrl?: string }[]
	lookupLoading: boolean
	onFocus: () => void
	onSearchChange: (v: string) => void
	onSearch: (v: string) => void
	onSelect: (r: ManagerRef) => void
	onRemove: () => void
	theme: any
	suggestion?: ManagerRef | null
	onApplySuggestion?: () => void
	onDismissSuggestion?: () => void
	showNoSuggestionHint?: boolean
}

const ManagerCard: React.FC<ManagerCardProps> = ({
	label,
	current,
	lookupActive,
	lookupSearch,
	lookupResults,
	lookupLoading,
	onFocus,
	onSearchChange,
	onSearch,
	onSelect,
	onRemove,
	theme,
	suggestion,
	onApplySuggestion,
	onDismissSuggestion,
	showNoSuggestionHint,
}) => (
	<Card
		size='small'
		style={{
			marginBottom: 16,
			borderRadius: 10,
			border: `1px solid ${theme.border}`,
			backgroundColor: theme.bg,
		}}
	>
		<Typography.Text strong style={{ fontSize: 13, color: theme.text }}>
			{label}
		</Typography.Text>
		{current ? (
			<div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
				<Avatar src={current.photoUrl} icon={<UserOutlined />} size='small' />
				<span style={{ color: theme.text }}>
					{current.name}{' '}
					<span style={{ color: theme.textTertiary, fontSize: 12 }}>({current.id})</span>
				</span>
				<Button type='text' danger size='small' onClick={onRemove}>
					Remove
				</Button>
			</div>
		) : (
			<Typography.Text style={{ display: 'block', marginTop: 4, color: theme.textSecondary }}>
				Not assigned
			</Typography.Text>
		)}
		<div style={{ marginTop: 8 }}>
			<Input.Search
				placeholder='Search staff by name or ID…'
				value={lookupActive ? lookupSearch : ''}
				onFocus={onFocus}
				onChange={(e) => onSearchChange(e.target.value)}
				onSearch={onSearch}
				loading={lookupLoading}
				enterButton
			/>
			{lookupActive && lookupResults.length > 0 && (
				<LookupList
					results={lookupResults}
					onSelect={onSelect}
					mutedColor={theme.textTertiary}
				/>
			)}
			{suggestion && (!current || current.id !== suggestion.id) ? (
				<div style={{ marginTop: 8 }}>
					<Typography.Text style={{ fontSize: 12, color: theme.textSecondary }}>
						Suggested from L1 hierarchy:{' '}
						<strong style={{ color: theme.text }}>
							{suggestion.name} ({suggestion.id})
						</strong>
					</Typography.Text>
					<Space size='small' style={{ marginLeft: 8 }}>
						<Button size='small' type='link' onClick={onApplySuggestion}>
							Apply
						</Button>
						<Button size='small' type='link' onClick={onDismissSuggestion}>
							Dismiss
						</Button>
					</Space>
				</div>
			) : null}
			{showNoSuggestionHint ? (
				<Typography.Text style={{ display: 'block', marginTop: 8, fontSize: 12 }} type='secondary'>
					No second-line suggestion found from the selected first-line manager.
				</Typography.Text>
			) : null}
		</div>
	</Card>
)

// ─── Unassigned Staff Grid ────────────────────────────────────────────────────

interface UnassignedStaffCardProps {
	staff: OrgStaff
	onEdit: (s: OrgStaff) => void
	theme: any
	actionLabel?: string
}

const UnassignedStaffCard: React.FC<UnassignedStaffCardProps> = ({
	staff,
	onEdit,
	theme,
	actionLabel = 'Assign Manager',
}) => {
	const { primary } = useThemeColors()
	const rankingColor = toRankingColor(staff.ranking, primary)

	return (
		<motion.div
			whileHover={{ y: -4, boxShadow: theme.cardHoverShadow }}
			transition={{ duration: 0.2 }}
		>
			<Card
				size='small'
				bordered={false}
				style={{
					textAlign: 'center',
					borderRadius: 20,
					border: `1px solid ${theme.border}`,
					boxShadow: theme.cardShadow,
					backgroundColor: theme.bg,
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
				}}
				bodyStyle={{
					padding: '0',
					display: 'flex',
					flexDirection: 'column',
					flex: 1,
				}}
			>
				{/* Accent header */}
				<div
					style={{
						height: 40,
						width: '100%',
						background: `linear-gradient(180deg, ${rankingColor}15 0%, ${theme.bg} 100%)`,
						position: 'relative',
						borderBottom: `1px solid ${theme.line}`,
					}}
				>
					<div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: rankingColor }} />
				</div>

				<div style={{ padding: '0 16px 16px', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
					<Avatar
						src={staff.photoUrl}
						icon={<UserOutlined />}
						size={60}
						style={{
							marginTop: -30,
							marginBottom: 12,
							border: `4px solid ${theme.bg}`,
							boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
							backgroundColor: theme.subBg,
						}}
					/>
					<div style={{ marginBottom: 12, textAlign: 'center' }}>
						<Typography.Text strong style={{ display: 'block', fontSize: 15, color: theme.text }}>
							{staff.nameEn || staff.nameKh || staff.id}
						</Typography.Text>
						{staff.nameKh && staff.nameEn && (
							<Typography.Text style={{ display: 'block', fontSize: 12, color: theme.textSecondary }}>
								{staff.nameKh}
							</Typography.Text>
						)}
						<Typography.Text
							style={{ display: 'block', fontSize: 11, color: theme.textTertiary, marginTop: 4, fontFamily: 'monospace' }}
						>
							ID: {staff.id}
						</Typography.Text>
					</div>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							gap: 4,
							marginBottom: 12,
						}}
					>
						{staff.ranking && (
							<Tag
								color={toRankingColor(staff.ranking, primary)}
								style={{ fontSize: 10, borderRadius: 12, margin: 0, border: 'none', padding: '1px 10px', fontWeight: 700 }}
							>
								{toRankingLabel(staff.ranking)}
							</Tag>
						)}
						{staff.role?.name && (
							<Typography.Text style={{ fontSize: 11, color: theme.textSecondary }}>
								{staff.role.name}
							</Typography.Text>
						)}
					</div>
				</div>
				<div style={{ borderTop: `1px solid ${theme.border}`, padding: '10px 12px', background: `${primary}08` }}>
					<Button
						type='primary'
						ghost
						size='middle'
						icon={<EditOutlined />}
						style={{ width: '100%', borderRadius: 12, height: 36, fontWeight: 600 }}
						onClick={() => onEdit(staff)}
					>
						{actionLabel}
					</Button>
				</div>
			</Card>
		</motion.div>
	)
}

// ─── Main Component ──────────────────────────────────────────────────────────

const StaffOrgChart: React.FC<StaffOrgChartProps> = ({
	viewMode: propViewMode,
	hideInternalHeader = false,
}) => {
	const theme = useThemeColors()
	const { isDarkMode } = useTheme()
	const { primary } = theme
	const [allStaff, setAllStaff] = useState<OrgStaff[]>([])
	const [adminStaffIds, setAdminStaffIds] = useState<Set<string>>(new Set())
	const [loading, setLoading] = useState(true)
	const [deptFilter, setDeptFilter] = useState<string | undefined>(undefined)
	const [searchId, setSearchId] = useState('')
	const [searchName, setSearchName] = useState('')
	const [activeTab, setActiveTab] = useState('chart')
	const [viewMode, setViewMode] = useState<'summary' | 'detail'>(() => {
		const saved = localStorage.getItem('org-chart-view-mode')
		return (saved as 'summary' | 'detail') || propViewMode || 'summary'
	})
	const [zoom, setZoom] = useState(1)

	useEffect(() => {
		localStorage.setItem('org-chart-view-mode', viewMode)
	}, [viewMode])

	// Edit drawer state
	const [editTarget, setEditTarget] = useState<OrgStaff | null>(null)
	const [editFirstMgr, setEditFirstMgr] = useState<ManagerRef | null>(null)
	const [editSecondMgr, setEditSecondMgr] = useState<ManagerRef | null>(null)
	const [editThirdMgr, setEditThirdMgr] = useState<ManagerRef | null>(null)
	const [lookupTarget, setLookupTarget] = useState<'first' | 'second' | 'third' | null>(null)
	const [lookupSearch, setLookupSearch] = useState('')
	const [lookupResults, setLookupResults] = useState<
		{ id: string; name: string; nameKh?: string; photoUrl?: string }[]
	>([])
	const [lookupLoading, setLookupLoading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [l2Suggestion, setL2Suggestion] = useState<ManagerRef | null>(null)
	const [l2SuggestionDismissed, setL2SuggestionDismissed] = useState(false)
	const [editAutoFillChain, setEditAutoFillChain] = useState(true)
	const [editAutoRanking, setEditAutoRanking] = useState(true)
	const [editSubjectRanking, setEditSubjectRanking] = useState<RankingTier>('executor')
	const [firstLineMgrRankingResolved, setFirstLineMgrRankingResolved] = useState<string | undefined>(
		undefined,
	)
	const rankingTouchedRef = useRef(false)
	const rankingSyncStaffIdRef = useRef<string | undefined>(undefined)
	const rankingSyncFlmIdRef = useRef<string | undefined>(undefined)

	// ── Data Fetching ──────────────────────────────────────────────────────────

	const fetchAllStaff = useCallback(async () => {
		setLoading(true)
		try {
			// Fetch all staff via cursor pagination loop
			const allPages: any[] = []
			let lastId: string | undefined = undefined
			while (true) {
				const params: any = { limit: 500 }
				if (lastId) params.lastId = lastId
				const res: any = await staffService.fetchListing(params)
				if (res?.statusCode !== 200) break
				const page = Array.isArray(res.data) ? res.data : []
				allPages.push(...page)
				if (!res?.hasNextPage || !res?.lastDocId) break
				lastId = res.lastDocId
			}

			const staff: OrgStaff[] = allPages.map((s: any) => ({
				id: s.id,
				nameKh: `${s.lastName || ''} ${s.firstName || ''}`.trim(),
				nameEn: s.nameInEnglish || '',
				phoneNumber: s.phoneNumber || '',
				ranking: s.ranking,
				photoUrl: s.photoUrl,
				departmentId: s.departmentId,
				role: s.role,
				firstLineManager: s.firstLineManager?.id ? s.firstLineManager : undefined,
				secondLineManager: s.secondLineManager?.id ? s.secondLineManager : undefined,
				thirdLineManager: s.thirdLineManager?.id ? s.thirdLineManager : undefined,
			}))
			const byId = new Map(staff.map((s) => [s.id, s] as const))
			const normalizedStaff: OrgStaff[] = staff.map((s) => {
				const normalizedFirst = s.firstLineManager?.id
					? (() => {
							const m = byId.get(s.firstLineManager!.id)
							return {
								id: s.firstLineManager!.id,
								name: toDisplayName(m) || s.firstLineManager!.name || s.firstLineManager!.id,
								photoUrl: m?.photoUrl || s.firstLineManager!.photoUrl,
							}
						})()
					: undefined
				const normalizedSecond = s.secondLineManager?.id
					? (() => {
							const m = byId.get(s.secondLineManager!.id)
							return {
								id: s.secondLineManager!.id,
								name:
									toDisplayName(m) || s.secondLineManager!.name || s.secondLineManager!.id,
								photoUrl: m?.photoUrl || s.secondLineManager!.photoUrl,
							}
						})()
					: undefined
				const normalizedThird = s.thirdLineManager?.id
					? (() => {
							const m = byId.get(s.thirdLineManager!.id)
							return {
								id: s.thirdLineManager!.id,
								name:
									toDisplayName(m) || s.thirdLineManager!.name || s.thirdLineManager!.id,
								photoUrl: m?.photoUrl || s.thirdLineManager!.photoUrl,
							}
						})()
					: undefined
				return {
					...s,
					firstLineManager: normalizedFirst,
					secondLineManager: normalizedSecond,
					thirdLineManager: normalizedThird,
				}
			})
			setAllStaff(normalizedStaff)

			const userRes: any = await userService.fetchListing()
			if (userRes?.statusCode === 200) {
				const ids = new Set<string>(
					(userRes.data || []).map((u: any) => u.staffId).filter(Boolean) as string[],
				)
				setAdminStaffIds(ids)
			}
		} catch (e) {
			console.error('Org chart fetch error:', e)
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		fetchAllStaff()
	}, [fetchAllStaff])

	// ── Derived Data ───────────────────────────────────────────────────────────

	const departments = useMemo(() => {
		const depts = new Set(allStaff.map((s) => s.departmentId).filter(Boolean) as string[])
		return Array.from(depts).sort()
	}, [allStaff])

	const adminStaff = useMemo(
		() => allStaff.filter((s) => adminStaffIds.has(s.id)),
		[allStaff, adminStaffIds],
	)

	const nonAdminStaff = useMemo(
		() => allStaff.filter((s) => !adminStaffIds.has(s.id)),
		[allStaff, adminStaffIds],
	)

	const staffById = useMemo(() => new Map(allStaff.map((s) => [s.id, s] as const)), [allStaff])

	const filteredStaff = useMemo(() => {
		let result = deptFilter
			? nonAdminStaff.filter((s) => s.departmentId === deptFilter)
			: nonAdminStaff
		if (searchId.trim()) {
			const q = searchId.trim().toLowerCase()
			result = result.filter((s) => s.id.toLowerCase().includes(q))
		}
		if (searchName.trim()) {
			const q = searchName.trim().toLowerCase()
			result = result.filter(
				(s) => s.nameEn.toLowerCase().includes(q) || s.nameKh.toLowerCase().includes(q),
			)
		}
		return result
	}, [nonAdminStaff, deptFilter, searchId, searchName])

	const noManagerStaff = useMemo(
		() => filteredStaff.filter((s) => !s.firstLineManager?.id),
		[filteredStaff],
	)

	const treeRoots = useMemo(() => buildTree(filteredStaff), [filteredStaff])

	// Only show roots that have at least one child — orphan staff (no manager, no reports) belong in "No Line Manager" tab only
	const chartRoots = useMemo(() => treeRoots.filter((n) => n.children.length > 0), [treeRoots])

	// ── Edit Handlers ──────────────────────────────────────────────────────────

	const openEdit = (staff: OrgStaff) => {
		const normalizeManagerRef = (
			ref?: { id: string; name: string; photoUrl?: string } | null,
		): ManagerRef | null => {
			if (!ref?.id) return null
			const source = allStaff.find((s) => s.id === ref.id)
			return {
				id: ref.id,
				name: toDisplayName(source) || ref.name || ref.id,
				photoUrl: source?.photoUrl || ref.photoUrl,
			}
		}

		setEditTarget(staff)
		setEditFirstMgr(normalizeManagerRef(staff.firstLineManager))
		setEditSecondMgr(normalizeManagerRef(staff.secondLineManager))
		setEditThirdMgr(normalizeManagerRef(staff.thirdLineManager))
		setLookupTarget(null)
		setLookupSearch('')
		setLookupResults([])
		setL2SuggestionDismissed(false)
		setEditAutoFillChain(true)
		setEditAutoRanking(true)
		const r = staff.ranking
		if (isRankingTier(r)) {
			setEditSubjectRanking(r)
			rankingTouchedRef.current = true
		} else {
			rankingTouchedRef.current = false
			setEditSubjectRanking('executor')
		}
	}

	const fetchStaffDetailPayload = useCallback(async (id: string): Promise<StaffDetailPayload | null> => {
		try {
			const res: any = await staffService.detail(id)
			return (res?.data || null) as StaffDetailPayload | null
		} catch {
			return null
		}
	}, [])

	useEffect(() => {
		if (!editTarget?.id) {
			setFirstLineMgrRankingResolved(undefined)
			return
		}
		const id = editFirstMgr?.id?.trim()
		if (!id) {
			setFirstLineMgrRankingResolved(undefined)
			return
		}
		const fromMap = staffById.get(id)?.ranking
		if (fromMap) {
			setFirstLineMgrRankingResolved(fromMap)
			return
		}
		let cancelled = false
		void (async () => {
			const d = await fetchStaffDetailPayload(id)
			if (!cancelled) setFirstLineMgrRankingResolved(d?.ranking)
		})()
		return () => {
			cancelled = true
		}
	}, [editTarget?.id, editFirstMgr?.id, staffById, fetchStaffDetailPayload])

	const computedSubjectRanking = useMemo((): RankingTier => {
		if (!editTarget) return 'executor'
		const fid = (editFirstMgr?.id || '').trim()
		const fromMap = fid ? staffById.get(fid)?.ranking : undefined
		const fr = (fromMap ?? firstLineMgrRankingResolved) || undefined
		return computeSubjectRanking(editTarget.id, fid || undefined, fr, allStaff)
	}, [editTarget, editFirstMgr?.id, staffById, firstLineMgrRankingResolved, allStaff])

	// Re-allow auto-suggest for ranking only when the same staff's 1st line manager changes (not on open).
	useEffect(() => {
		if (!editTarget?.id) {
			rankingSyncStaffIdRef.current = undefined
			rankingSyncFlmIdRef.current = undefined
			return
		}
		const sid = editTarget.id.trim()
		const fid = (editFirstMgr?.id || '').trim()
		const prevSid = rankingSyncStaffIdRef.current
		const prevFid = rankingSyncFlmIdRef.current
		rankingSyncStaffIdRef.current = sid
		rankingSyncFlmIdRef.current = fid
		if (prevSid === sid && prevFid !== undefined && prevFid !== fid) {
			rankingTouchedRef.current = false
		}
	}, [editTarget?.id, editFirstMgr?.id, editTarget])

	// Auto-suggest ranking into the dropdown until the user edits it (or opened with a stored ranking).
	useEffect(() => {
		if (!editTarget || rankingTouchedRef.current) return
		setEditSubjectRanking(computedSubjectRanking)
	}, [editTarget, computedSubjectRanking])

	// Live 2nd/3rd line when auto-fill is on (uses staff detail when list rows omit manager fields).
	useEffect(() => {
		if (!editTarget || !editAutoFillChain) return
		if (!editFirstMgr?.id) {
			setEditSecondMgr(null)
			setEditThirdMgr(null)
			return
		}
		let cancelled = false
		void (async () => {
			const out = await resolveSecondThirdFromFirstLineAsync(
				editTarget.id,
				editFirstMgr,
				staffById,
				fetchStaffDetailPayload,
			)
			if (cancelled) return
			setEditSecondMgr(out.second)
			setEditThirdMgr(out.third)
		})()
		return () => {
			cancelled = true
		}
	}, [
		editTarget?.id,
		editFirstMgr?.id,
		editAutoFillChain,
		staffById,
		fetchStaffDetailPayload,
	])

	const isValidL2Candidate = useCallback(
		(candidate?: { id?: string | null } | null, firstMgr?: { id?: string | null } | null) => {
			const candidateId = (candidate?.id || '').trim()
			if (!candidateId) return false
			const staffId = (editTarget?.id || '').trim()
			const firstId = (firstMgr?.id || editFirstMgr?.id || '').trim()
			if (staffId && candidateId === staffId) return false
			if (firstId && candidateId === firstId) return false
			return true
		},
		[editTarget?.id, editFirstMgr?.id],
	)

	const refreshL2Suggestion = useCallback(
		async (firstMgrId?: string | null, firstMgrRef?: ManagerRef | null) => {
			const id = (firstMgrId || '').trim()
			if (!id) {
				setL2Suggestion(null)
				return
			}
			try {
				const res: any = await staffService.detail(id)
				const d = res?.data || {}
				const candidatePrimary = d?.firstLineManager?.id
					? {
							id: d.firstLineManager.id as string,
							name: d.firstLineManager.name as string,
							photoUrl: d.firstLineManager.photoUrl as string | undefined,
						}
					: null
				const candidateFallback =
					!candidatePrimary && d?.secondLineManager?.id
						? {
								id: d.secondLineManager.id as string,
								name: d.secondLineManager.name as string,
								photoUrl: d.secondLineManager.photoUrl as string | undefined,
							}
						: null
				const candidate = candidatePrimary || candidateFallback
				if (!isValidL2Candidate(candidate, firstMgrRef || null)) {
					setL2Suggestion(null)
					return
				}
				setL2Suggestion(candidate)
			} catch {
				setL2Suggestion(null)
			}
		},
		[isValidL2Candidate],
	)

	const onLookupSearch = async (q: string) => {
		if (!q.trim()) return
		setLookupLoading(true)
		try {
			const results = await staffService.autocomplete(q.trim())
			setLookupResults(results || [])
		} catch {
			setLookupResults([])
		} finally {
			setLookupLoading(false)
		}
	}

	const clearLookup = () => {
		setLookupResults([])
		setLookupSearch('')
		setLookupTarget(null)
	}

	const onSelectManager = (ref: ManagerRef) => {
		if (lookupTarget === 'first') {
			setEditFirstMgr(ref)
			setL2SuggestionDismissed(false)
			refreshL2Suggestion(ref.id, ref)
		} else if (lookupTarget === 'second') {
			setEditSecondMgr(ref)
		} else if (lookupTarget === 'third') {
			setEditThirdMgr(ref)
		}
		clearLookup()
	}

	const onSave = async () => {
		if (!editTarget) return
		const staffId = (editTarget.id || '').trim()
		let firstMgr = editFirstMgr
		let secondMgr = editSecondMgr
		let thirdMgr = editThirdMgr

		if (!firstMgr?.id) {
			secondMgr = null
			thirdMgr = null
		} else if (editAutoFillChain) {
			const resolved = await resolveSecondThirdFromFirstLineAsync(
				staffId,
				firstMgr,
				staffById,
				fetchStaffDetailPayload,
			)
			secondMgr = resolved.second
			thirdMgr = resolved.third
		}

		const firstMgrId = (firstMgr?.id || '').trim()
		const secondMgrId = (secondMgr?.id || '').trim()
		const thirdMgrId = (thirdMgr?.id || '').trim()
		if (staffId && firstMgrId && staffId === firstMgrId) {
			message.error('1st line manager cannot be the same as this staff')
			return
		}
		if (staffId && secondMgrId && staffId === secondMgrId) {
			message.error('2nd line manager cannot be the same as this staff')
			return
		}
		if (staffId && thirdMgrId && staffId === thirdMgrId) {
			message.error('3rd line manager cannot be the same as this staff')
			return
		}
		if (firstMgrId && secondMgrId && firstMgrId === secondMgrId) {
			message.error('1st and 2nd line managers must be different people')
			return
		}
		if (firstMgrId && thirdMgrId && firstMgrId === thirdMgrId) {
			message.error('1st and 3rd line managers must be different people')
			return
		}
		if (secondMgrId && thirdMgrId && secondMgrId === thirdMgrId) {
			message.error('2nd and 3rd line managers must be different people')
			return
		}

		const resolveSeniorityScore = async (mgrId: string): Promise<number> => {
			const row = allStaff.find((s) => s.id === mgrId)
			if (row?.ranking) return seniorityScore(row.ranking)
			const d = await fetchStaffDetailPayload(mgrId)
			return seniorityScore(d?.ranking)
		}

		const [s1, s2, s3] = await Promise.all([
			firstMgrId ? resolveSeniorityScore(firstMgrId) : Promise.resolve(0),
			secondMgrId ? resolveSeniorityScore(secondMgrId) : Promise.resolve(0),
			thirdMgrId ? resolveSeniorityScore(thirdMgrId) : Promise.resolve(0),
		])

		if (firstMgrId && secondMgrId && s1 > s2) {
			message.error(
				'Invalid reporting line: 1st line manager cannot be a higher level than 2nd line. Levels must run from the top down: L3 (3rd line) → L2 (2nd line) → L1 (1st line) → Executor.',
			)
			return
		}
		if (secondMgrId && thirdMgrId && s2 > s3) {
			message.error(
				'Invalid reporting line: 2nd line manager cannot be a higher level than 3rd line. The most senior manager belongs in 3rd line (L3), then L2, L1, Executor.',
			)
			return
		}
		if (firstMgrId && thirdMgrId && !secondMgrId && s1 > s3) {
			message.error(
				'Invalid reporting line: with only 1st and 3rd line set, 3rd line must be the same or a higher level than 1st line (L3 → … → Executor).',
			)
			return
		}

		setSaving(true)
		try {
			const payload: Record<string, unknown> = {
				id: editTarget.id,
				phoneNumber: editTarget.phoneNumber,
				firstLineManager: firstMgr ?? null,
				secondLineManager: secondMgr ?? null,
				thirdLineManager: thirdMgr ?? null,
				ranking: editSubjectRanking,
			}

			await staffService.update(payload)

			if (editAutoRanking) {
				const patchRankingIfHigher = async (
					targetId: string,
					minTier: RankingTier,
				): Promise<void> => {
					let row = allStaff.find((s) => s.id === targetId)
					if (!row?.phoneNumber) {
						try {
							const res: any = await staffService.detail(targetId)
							const d = res?.data || {}
							row = {
								id: d.id || targetId,
								nameKh: `${d.lastName || ''} ${d.firstName || ''}`.trim(),
								nameEn: d.nameInEnglish || '',
								phoneNumber: d.phoneNumber || '',
								ranking: d.ranking,
							}
						} catch {
							return
						}
					}
					if (!row?.phoneNumber) return
					const merged = maxRankingTier(row.ranking, minTier)
					if (merged === row.ranking) return
					await staffService.update({
						id: targetId,
						phoneNumber: row.phoneNumber,
						ranking: merged,
					})
				}

				const chainPatches: Promise<void>[] = []
				if (firstMgrId) chainPatches.push(patchRankingIfHigher(firstMgrId, 'manager_level_1'))
				if (secondMgrId) chainPatches.push(patchRankingIfHigher(secondMgrId, 'manager_level_2'))
				if (thirdMgrId) chainPatches.push(patchRankingIfHigher(thirdMgrId, 'manager_level_3'))
				await Promise.all(chainPatches)
			}

			message.success('Manager assignment updated')
			setEditTarget(null)
			setL2Suggestion(null)
			setL2SuggestionDismissed(false)
			await fetchAllStaff()
		} catch (e: any) {
			message.error(e?.response?.data?.message || 'Failed to save manager assignment')
		} finally {
			setSaving(false)
		}
	}

	useEffect(() => {
		if (!editTarget) {
			setL2Suggestion(null)
			return
		}
		if (!editFirstMgr?.id) {
			setL2Suggestion(null)
			return
		}
		refreshL2Suggestion(editFirstMgr.id, editFirstMgr)
	}, [editTarget, editFirstMgr?.id, refreshL2Suggestion])

	// ── Render ─────────────────────────────────────────────────────────────────

	const unassignedCount = noManagerStaff.length
	const adminStaffCount = adminStaff.length
	const badgeNoLineManagerColor = '#f5b5b5'
	const badgeAdminColor = '#1677ff'
	const fullChainVerifiedCount = filteredStaff.filter(isExecutorWithFullManagerChain).length
	const incompleteChainStaff = useMemo(
		() =>
			filteredStaff.filter((s) => {
				const assignedCount =
					(s.firstLineManager?.id ? 1 : 0) +
					(s.secondLineManager?.id ? 1 : 0) +
					(s.thirdLineManager?.id ? 1 : 0)
				return assignedCount === 1 || assignedCount === 2
			}),
		[filteredStaff],
	)
	const incompleteChainCount = incompleteChainStaff.length

	const deptFilterSelect = (
		<div
			style={{
				marginBottom: 16,
				padding: '16px',
				background: theme.bg,
				borderRadius: 16,
				border: `1px solid ${theme.border}`,
				boxShadow: theme.cardShadow,
			}}
		>
			<Space wrap size={16} style={{ width: '100%', justifyContent: 'space-between' }}>
				<Space wrap size={12}>
					<div style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.textSecondary }}>
						<FilterOutlined />
						<Typography.Text strong style={{ fontSize: 13, color: theme.textSecondary }}>Filters:</Typography.Text>
					</div>
					<Select
						allowClear
						placeholder='All Departments'
						style={{ width: 200 }}
						value={deptFilter}
						onChange={setDeptFilter}
						options={departments.map((d) => ({ value: d, label: d }))}
					/>
					<Input
						allowClear
						placeholder='ID'
						prefix={<InfoCircleOutlined style={{ opacity: 0.5 }} />}
						style={{ width: 140 }}
						value={searchId}
						onChange={(e) => setSearchId(e.target.value)}
					/>
					<Input
						allowClear
						placeholder='Name'
						prefix={<SearchOutlined style={{ opacity: 0.5 }} />}
						style={{ width: 180 }}
						value={searchName}
						onChange={(e) => setSearchName(e.target.value)}
					/>
				</Space>

				<Space size={12}>
					<Typography.Text type='secondary' style={{ fontSize: 12, fontWeight: 500 }}>VIEW MODE</Typography.Text>
					<ViewModeSwitcher
						value={viewMode}
						onChange={setViewMode}
						theme={theme}
						primary={primary}
					/>
					<div style={{ height: 24, width: 1, background: theme.border, margin: '0 4px' }} />
					<Button.Group size='small'>
						<Button icon={<LeftOutlined />} onClick={() => setZoom(Math.max(0.5, zoom - 0.1))} />
						<Button style={{ width: 50, fontSize: 11 }}>{Math.round(zoom * 100)}%</Button>
						<Button icon={<RightOutlined />} onClick={() => setZoom(Math.min(1.5, zoom + 0.1))} />
					</Button.Group>
				</Space>
			</Space>
		</div>
	)

	const tabItems = [
		{
			key: 'chart',
			label: (
				<span>
					<ApartmentOutlined /> Org Chart{' '}
					{fullChainVerifiedCount > 0 && (
						<Badge
							count={fullChainVerifiedCount}
							overflowCount={999999}
							style={{ marginLeft: 4, backgroundColor: '#22c55e' }}
						/>
					)}
				</span>
			),
			children: (
				<AnimatePresence mode='wait'>
					<motion.div
						key="chart-tab"
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -10 }}
					>
						{deptFilterSelect}
						<OrgLegend theme={theme} primary={primary} />
						{loading ? (
							<div style={{ textAlign: 'center', padding: 80, background: theme.bg, borderRadius: 16 }}>
								<Spin size='large' tip="Building organization tree..." />
							</div>
						) : chartRoots.length === 0 ? (
							<div style={{ background: theme.bg, padding: 60, borderRadius: 16, border: `1px solid ${theme.border}` }}>
								<Empty description='No staff found with the current filters' />
							</div>
						) : (
							<div
								style={{
									width: '100%',
									overflow: 'auto',
									padding: '24px',
									background: theme.bg,
									borderRadius: 16,
									border: `1px solid ${theme.border}`,
									boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.05)',
									minHeight: '400px',
									perspective: 2000,
								}}
							>
								<AnimatePresence mode='wait'>
									<motion.div
										key={viewMode}
										initial={{ rotateY: 20, opacity: 0, x: 40, scale: 0.95 }}
										animate={{ rotateY: 0, opacity: 1, x: 0, scale: 1 }}
										exit={{ rotateY: -20, opacity: 0, x: -40, scale: 0.95 }}
										transition={{ duration: 0.7, ease: [0.23, 1, 0.32, 1] }}
										style={{
											display: 'inline-flex',
											flexDirection: 'row',
											alignItems: 'flex-start',
											minWidth: 'max-content',
											transformOrigin: 'center center',
											transformStyle: 'preserve-3d',
										}}
									>
										<div
											style={{
												transform: `scale(${zoom})`,
												transformOrigin: 'top left',
												transition: 'transform 0.2s ease-out',
											}}
										>
											{chartRoots.map((node) => (
												<OrgTreeNode
													key={node.staff.id}
													node={node}
													onEdit={openEdit}
													theme={theme}
													viewMode={viewMode}
													isRoot
												/>
											))}
										</div>
									</motion.div>
								</AnimatePresence>
							</div>
						)}
					</motion.div>
				</AnimatePresence>
			),
		},
		{
			key: 'unassigned',
			label: (
				<span>
					<WarningOutlined
						style={{ color: unassignedCount > 0 ? badgeNoLineManagerColor : undefined }}
					/>{' '}
					No Line Manager{' '}
					{unassignedCount > 0 && (
						<Badge
							count={unassignedCount}
							overflowCount={999999}
							style={{
								marginLeft: 4,
								backgroundColor: badgeNoLineManagerColor,
								color: '#9f1239',
							}}
						/>
					)}
				</span>
			),
			children: (
				<AnimatePresence mode='wait'>
					<motion.div
						key="unassigned-tab"
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -10 }}
					>
						{deptFilterSelect}
						{loading ? (
							<div style={{ textAlign: 'center', padding: 80, background: theme.bg, borderRadius: 16 }}>
								<Spin size='large' tip="Loading unassigned staff..." />
							</div>
						) : noManagerStaff.length === 0 ? (
							<div style={{ background: theme.bg, padding: 60, borderRadius: 16, border: `1px solid ${theme.border}` }}>
								<Empty description='All staff have a 1st line manager assigned ✓' />
							</div>
						) : (
							<div style={{ background: theme.bg, padding: '24px', borderRadius: 16, border: `1px solid ${theme.border}` }}>
								<Typography.Text
									type='secondary'
									style={{ display: 'block', marginBottom: 20, fontSize: 13 }}
								>
									<InfoCircleOutlined style={{ marginRight: 8, color: primary }} />
									{unassignedCount} staff member{unassignedCount !== 1 ? 's' : ''}{' '}
									without a 1st line manager. Click <strong>Assign Manager</strong> to
									configure their hierarchy.
								</Typography.Text>
								<div
									style={{
										display: 'grid',
										gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
										gap: 20,
									}}
								>
									{noManagerStaff.map((staff) => (
										<UnassignedStaffCard
											key={staff.id}
											staff={staff}
											onEdit={openEdit}
											theme={theme}
										/>
									))}
								</div>
							</div>
						)}
					</motion.div>
				</AnimatePresence>
			),
		},
		{
			key: 'incomplete-chain',
			label: (
				<span>
					<WarningOutlined
						style={{ color: incompleteChainCount > 0 ? '#f59e0b' : undefined }}
					/>{' '}
					Incomplete Chain{' '}
					{incompleteChainCount > 0 && (
						<Badge
							count={incompleteChainCount}
							overflowCount={999999}
							style={{ marginLeft: 4, backgroundColor: '#f59e0b' }}
						/>
					)}
				</span>
			),
			children: (
				<AnimatePresence mode='wait'>
					<motion.div
						key="incomplete-chain-tab"
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -10 }}
					>
						{deptFilterSelect}
						{loading ? (
							<div style={{ textAlign: 'center', padding: 80, background: theme.bg, borderRadius: 16 }}>
								<Spin size='large' tip="Loading incomplete chain staff..." />
							</div>
						) : incompleteChainCount === 0 ? (
							<div style={{ background: theme.bg, padding: 60, borderRadius: 16, border: `1px solid ${theme.border}` }}>
								<Empty description='All staff are full chain verified ✓' />
							</div>
						) : (
							<div style={{ background: theme.bg, padding: '24px', borderRadius: 16, border: `1px solid ${theme.border}` }}>
								<Typography.Text
									type='secondary'
									style={{ display: 'block', marginBottom: 20, fontSize: 13 }}
								>
									<InfoCircleOutlined style={{ marginRight: 8, color: '#f59e0b' }} />
									{incompleteChainCount} staff member
									{incompleteChainCount !== 1 ? 's' : ''} still not full-chain verified.
									Click <strong>Complete Chain</strong> to update.
								</Typography.Text>
								<div
									style={{
										display: 'grid',
										gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
										gap: 20,
									}}
								>
									{incompleteChainStaff.map((staff) => (
										<UnassignedStaffCard
											key={staff.id}
											staff={staff}
											onEdit={openEdit}
											theme={theme}
											actionLabel='Complete Chain'
										/>
									))}
								</div>
							</div>
						)}
					</motion.div>
				</AnimatePresence>
			),
		},
		{
			key: 'admin',
			label: (
				<span>
					<CrownOutlined style={{ color: badgeAdminColor }} /> Admin Staff{' '}
					{adminStaffCount > 0 && (
						<Badge
							count={adminStaffCount}
							overflowCount={999999}
							style={{ marginLeft: 4, backgroundColor: badgeAdminColor }}
						/>
					)}
				</span>
			),
			children: (
				<AnimatePresence mode='wait'>
					<motion.div
						key="admin-tab"
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -10 }}
					>
						{loading ? (
							<div style={{ textAlign: 'center', padding: 80, background: theme.bg, borderRadius: 16 }}>
								<Spin size='large' />
							</div>
						) : adminStaff.length === 0 ? (
							<div style={{ background: theme.bg, padding: 60, borderRadius: 16, border: `1px solid ${theme.border}` }}>
								<Empty description='No staff linked to admin users' />
							</div>
						) : (
							<div style={{ background: theme.bg, padding: '24px', borderRadius: 16, border: `1px solid ${theme.border}` }}>
								<Typography.Text
									type='secondary'
									style={{ display: 'block', marginBottom: 20, fontSize: 13 }}
								>
									<CrownOutlined style={{ marginRight: 8, color: '#1677ff' }} />
									{adminStaffCount} staff member{adminStaffCount !== 1 ? 's' : ''} linked to
									admin portal users. These staff are excluded from the Org Chart and No Line
									Manager tabs.
								</Typography.Text>
								<div
									style={{
										display: 'grid',
										gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
										gap: 20,
									}}
								>
									{adminStaff.map((staff) => (
										<motion.div key={staff.id} whileHover={{ y: -4, boxShadow: theme.cardHoverShadow }} transition={{ duration: 0.2 }}>
											<Card
												size='small'
												bordered={false}
												style={{
													textAlign: 'center',
													borderRadius: 20,
													border: `1px solid ${isDarkMode ? '#1677ff44' : '#1677ff22'}`,
													boxShadow: theme.cardShadow,
													backgroundColor: theme.bg,
													height: '100%',
													display: 'flex',
													flexDirection: 'column',
													overflow: 'hidden',
												}}
												bodyStyle={{
													padding: '0',
													display: 'flex',
													flexDirection: 'column',
													flex: 1,
												}}
											>
												<div
													style={{
														height: 40,
														width: '100%',
														background: `linear-gradient(180deg, #1677ff15 0%, ${theme.bg} 100%)`,
														borderBottom: `1px solid ${theme.border}`,
														position: 'relative',
													}}
												>
													<div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: '#1677ff' }} />
												</div>

												<div style={{ padding: '0 16px 16px', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
													<Avatar
														src={staff.photoUrl}
														icon={<UserOutlined />}
														size={60}
														style={{
															marginTop: -30,
															marginBottom: 12,
															border: `4px solid ${theme.bg}`,
															boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
															backgroundColor: theme.subBg,
														}}
													/>
													<div style={{ marginBottom: 12, textAlign: 'center' }}>
														<Typography.Text
															strong
															style={{ display: 'block', fontSize: 15, color: theme.text }}
														>
															{staff.nameEn || staff.nameKh || staff.id}
														</Typography.Text>
														{staff.nameKh && staff.nameEn && (
															<Typography.Text
																style={{
																	display: 'block',
																	fontSize: 12,
																	color: theme.textSecondary,
																}}
															>
																{staff.nameKh}
															</Typography.Text>
														)}
														<Typography.Text
															type='secondary'
															style={{
																display: 'block',
																fontSize: 11,
																color: theme.textTertiary,
																marginTop: 4,
																fontFamily: 'monospace',
															}}
														>
															ID: {staff.id}
														</Typography.Text>
													</div>
													<div
														style={{
															display: 'flex',
															flexDirection: 'column',
															alignItems: 'center',
															gap: 4,
															marginBottom: 8,
														}}
													>
														{staff.departmentId && (
															<Tag style={{ fontSize: 10, borderRadius: 12, margin: 0, padding: '1px 10px', border: 'none' }}>
																{staff.departmentId}
															</Tag>
														)}
														{staff.role?.name && (
															<Typography.Text style={{ fontSize: 11, color: theme.textSecondary }}>
																{staff.role.name}
															</Typography.Text>
														)}
													</div>
												</div>
												<div style={{ borderTop: `1px solid ${theme.border}`, padding: '10px 12px', background: '#1677ff08' }}>
													<Tag
														color='blue'
														icon={<CrownOutlined />}
														style={{ fontSize: 11, borderRadius: 12, width: '100%', textAlign: 'center', padding: '4px 0', margin: 0, border: 'none', fontWeight: 600 }}
													>
														Admin User
													</Tag>
												</div>
											</Card>
										</motion.div>
									))}
								</div>
							</div>
						)}
					</motion.div>
				</AnimatePresence>
			),
		},
	]
	const orderedTabItems = [
		tabItems.find((t) => t.key === 'chart'),
		tabItems.find((t) => t.key === 'incomplete-chain'),
		tabItems.find((t) => t.key === 'unassigned'),
		tabItems.find((t) => t.key === 'admin'),
	].filter(Boolean)

	return (
		<div
			style={{
				backgroundColor: theme.chartBg,
				padding: 24,
				borderRadius: 16,
				transition: 'all 0.3s ease',
				position: 'relative',
				minHeight: '600px',
			}}
		>
			{!hideInternalHeader && (
				<div style={{ marginBottom: 20 }}>
					<Typography.Title level={3} style={{ marginBottom: 0, color: theme.text }}>
						<ApartmentOutlined /> Staff Org Chart
					</Typography.Title>
					<Typography.Text style={{ display: 'block', color: theme.textSecondary }}>
						View and configure staff reporting hierarchy (1st &amp; 2nd line managers).
					</Typography.Text>
				</div>
			)}

			<Tabs
				items={orderedTabItems}
				activeKey={activeTab}
				onChange={setActiveTab}
				className={isDarkMode ? 'dark-tabs' : ''}
			/>

			{isDarkMode && (
				<style>{`
					.dark-tabs .ant-tabs-tab { color: #94a3b8 !important; }
					.dark-tabs .ant-tabs-tab-active .ant-tabs-tab-btn { color: #f8fafc !important; }
					.dark-tabs .ant-tabs-ink-bar { background: ${primary} !important; }
					.dark-tabs .ant-tabs-nav::before { border-bottom-color: #334155 !important; }
				`}</style>
			)}

			{/* ── Edit Manager Drawer ── */}
			<Drawer
				title={
					editTarget ? (
						<Space style={{ color: theme.text }}>
							<Avatar
								src={editTarget.photoUrl}
								icon={<UserOutlined />}
								size='small'
							/>
							<span>
								Assign Managers —{' '}
								<strong>
									{editTarget.nameEn || editTarget.nameKh || editTarget.id}
								</strong>
								<span style={{ fontWeight: 400, color: theme.textSecondary, marginLeft: 6 }}>
									({editTarget.id})
								</span>
							</span>
						</Space>
					) : (
						'Assign Managers'
					)
				}
				open={!!editTarget}
				onClose={() => {
					setEditTarget(null)
					setL2Suggestion(null)
					setL2SuggestionDismissed(false)
					setEditAutoFillChain(true)
					setEditAutoRanking(true)
				}}
				width={480}
				headerStyle={{
					backgroundColor: theme.bg,
					borderBottom: `1px solid ${theme.border}`,
				}}
				bodyStyle={{ backgroundColor: theme.bg }}
				footerStyle={{ backgroundColor: theme.bg, borderTop: `1px solid ${theme.border}` }}
				footer={
					<Space style={{ justifyContent: 'flex-end', width: '100%', display: 'flex' }}>
						<Button
							onClick={() => {
								setEditTarget(null)
								setL2Suggestion(null)
								setL2SuggestionDismissed(false)
								setEditAutoFillChain(true)
								setEditAutoRanking(true)
							}}
							style={{
								color: theme.text,
								borderColor: theme.border,
								backgroundColor: 'transparent',
							}}
						>
							Cancel
						</Button>
						<Button type='primary' loading={saving} onClick={onSave}>
							Save
						</Button>
					</Space>
				}
			>
				{editTarget && (
					<>
						<div
							style={{
								marginBottom: 16,
								paddingBottom: 14,
								borderBottom: `1px solid ${theme.border}`,
							}}
						>
							<Space direction='vertical' size={10} style={{ width: '100%' }}>
								<Checkbox
									checked={editAutoFillChain}
									onChange={(e) => setEditAutoFillChain(e.target.checked)}
									style={{ color: theme.text }}
								>
									Auto-fill 2nd / 3rd line from the 1st line manager&apos;s chain
								</Checkbox>
								<Checkbox
									checked={editAutoRanking}
									onChange={(e) => {
										const on = e.target.checked
										setEditAutoRanking(on)
										if (on) {
											rankingTouchedRef.current = false
											setEditSubjectRanking(computedSubjectRanking)
										}
									}}
									style={{ color: theme.text }}
								>
									Update ranking for this staff and the assigned manager chain (managers
									are only upgraded, not downgraded)
								</Checkbox>
							</Space>
						</div>
						<div style={{ marginBottom: 16 }}>
							<Typography.Text
								strong
								style={{ display: 'block', marginBottom: 6, color: theme.text }}
							>
								Staff ranking
							</Typography.Text>
							<Select
								style={{ width: '100%' }}
								value={editSubjectRanking}
								onChange={(v) => {
									rankingTouchedRef.current = true
									setEditSubjectRanking(v as RankingTier)
								}}
								options={RANKING_SELECT_OPTIONS}
							/>
							<Typography.Text
								type='secondary'
								style={{ display: 'block', marginTop: 8, fontSize: 12, color: theme.textSecondary }}
							>
								Auto-suggest when there is no stored ranking, or after you change the 1st line
								manager (or turn &quot;Update ranking…&quot; back on). If this staff already has a
								ranking, it loads as the default until you change it.
							</Typography.Text>
						</div>
						<ManagerCard
							label='1st Line Manager (Direct Manager)'
							current={editFirstMgr}
							lookupActive={lookupTarget === 'first'}
							lookupSearch={lookupSearch}
							lookupResults={lookupResults}
							lookupLoading={lookupLoading && lookupTarget === 'first'}
							onFocus={() => setLookupTarget('first')}
							onSearchChange={(v) => {
								setLookupSearch(v)
								if (lookupTarget !== 'first') setLookupTarget('first')
							}}
							onSearch={onLookupSearch}
							onSelect={onSelectManager}
							onRemove={() => {
								setEditFirstMgr(null)
								setL2Suggestion(null)
								setL2SuggestionDismissed(false)
								if (lookupTarget === 'first') clearLookup()
							}}
							theme={theme}
						/>
						<ManagerCard
							label='2nd Line Manager'
							current={editSecondMgr}
							lookupActive={lookupTarget === 'second'}
							lookupSearch={lookupSearch}
							lookupResults={lookupResults}
							lookupLoading={lookupLoading && lookupTarget === 'second'}
							onFocus={() => setLookupTarget('second')}
							onSearchChange={(v) => {
								setLookupSearch(v)
								if (lookupTarget !== 'second') setLookupTarget('second')
							}}
							onSearch={onLookupSearch}
							onSelect={onSelectManager}
							onRemove={() => {
								setEditSecondMgr(null)
								setL2SuggestionDismissed(false)
								if (lookupTarget === 'second') clearLookup()
							}}
							suggestion={l2SuggestionDismissed ? null : l2Suggestion}
							onApplySuggestion={() => {
								if (l2Suggestion) setEditSecondMgr(l2Suggestion)
							}}
							onDismissSuggestion={() => setL2SuggestionDismissed(true)}
							showNoSuggestionHint={
								!!editFirstMgr &&
								!editSecondMgr &&
								!l2SuggestionDismissed &&
								!l2Suggestion
							}
							theme={theme}
						/>
						<ManagerCard
							label='3rd Line Manager'
							current={editThirdMgr}
							lookupActive={lookupTarget === 'third'}
							lookupSearch={lookupSearch}
							lookupResults={lookupResults}
							lookupLoading={lookupLoading && lookupTarget === 'third'}
							onFocus={() => setLookupTarget('third')}
							onSearchChange={(v) => {
								setLookupSearch(v)
								if (lookupTarget !== 'third') setLookupTarget('third')
							}}
							onSearch={onLookupSearch}
							onSelect={onSelectManager}
							onRemove={() => {
								setEditThirdMgr(null)
								if (lookupTarget === 'third') clearLookup()
							}}
							theme={theme}
						/>
					</>
				)}
			</Drawer>
		</div>
	)
}

export default StaffOrgChart
